/**
 * previewMock — LOCAL, NO-BACKEND demo data for the TravelTrucks OS board.
 *
 * When the OS runs on localhost with no real backend (the preview setup), the
 * Desk's `GET /api/admin/desk/board` fails and you get a "Couldn't load the
 * Desk" card. This module patches window.fetch to serve representative Base
 * Camp sample data for the desk read endpoints, so the dashboard renders fully
 * with no login and no console scripts.
 *
 * ACTIVATION is decided in index.js (localhost + no/dead backend). The moment a
 * real REACT_APP_BACKEND_URL is set, this is NOT installed and real data flows.
 * Only GET reads are mocked; writes return a benign {} so the UI stays happy.
 * Remove/disable before shipping to real users.
 */

const BOARD = {
  now: {
    item_id: 'now-1',
    title: 'Revision request — Coastal Diesel',
    sub: 'Wants staged billing on the $19,800 fleet bars',
    action: 'Open draft',
    action_type: 'draft',
    day_arc: 1,
  },
  pulse: [
    { icon: 'reply', title: 'Quote sent — LC200 lift', sub: '0d since sent · $5,311' },
    { icon: 'zap', title: 'Order diff breathers — Patrol', sub: '1d stale · parts', hot: true },
    { icon: 'user', title: 'Jarrah W. — canopy enquiry', sub: 'Walk-in follow-up' },
    { icon: 'file-check', title: 'ARB — order dispatched', sub: 'Snorkel + breathers tracking' },
  ],
  clients: [
    { item_id: 'c1', title: 'Coastal Diesel', sub: 'Fleet bars ×4 · $19,800', heat: 'hot' },
    { item_id: 'c2', title: 'Bayside 4x4', sub: 'Recovery kit · $3,420', heat: 'warm' },
    { item_id: 'c3', title: 'GT Refrigeration', sub: 'Snorkel pack · $1,240', heat: 'warm' },
    { item_id: 'c4', title: 'J. Blake', sub: 'Hilux N80 canopy · $8,450', heat: 'fresh' },
  ],
  wins: [
    { title: 'LC79 bullbar fitted' },
    { title: 'Canopy — J. Blake', value: 8450 },
    { title: 'Diff breathers restock' },
    { title: 'Bayside recovery kit', value: 3420 },
  ],
  parking: [
    { item_id: 'p1', title: 'Build-package pricing tiers (Tourer / Remote / Comp)' },
    { item_id: 'p2', title: 'Loan vehicle while builds are in the bay' },
    { item_id: 'p3', title: 'Before/after reel for finished rigs' },
  ],
  money: {
    quotes_out: 34120,
    likely_this_month: 12600,
    won_month: 18730,
    overdue: 4880,
  },
};

const LISTS = {
  lists: [
    { list_id: 'jobs', name: 'Jobs to do', kind: 'list' },
    { list_id: 'ideas', name: 'Ideas', kind: 'list' },
  ],
  items: {
    jobs: [
      { item_id: 'j1', title: 'Fit 79 Series bullbar — bay 2' },
      { item_id: 'j2', title: 'Wheel align — Ranger after lift' },
      { item_id: 'j3', title: 'Pressure test long-range tank' },
    ],
    ideas: [
      { item_id: 'i1', title: 'Build-package pricing tiers' },
      { item_id: 'i2', title: 'Loan vehicle while builds are in the bay' },
    ],
  },
};

const PROFILES = { profiles: [
  { profile_id: 'default', name: 'Base Camp', auto_kind: 'lg' },
  { profile_id: 'tablet', name: 'Tablet', auto_kind: 'md' },
  { profile_id: 'phone', name: 'Phone', auto_kind: 'sm' },
] };

// Empty layouts -> the Desk falls back to its built-in DEFAULT_LAYOUTS.
// Full-width tiles get explicit geometry (w:12 lg / 8 md / 2 sm) so the preview
// shows them at their real size; the rest auto-place below.
const LAYOUT = {
  tiles: ['quotepipeline', 'fittingbay', 'gilliland', 'now', 'ai', 'debrief', 'actions', 'pulse', 'money', 'clients', 'wins', 'parking'],
  layouts: {
    lg: [
      { i: 'quotepipeline', x: 0, y: 0, w: 12, h: 6 }, { i: 'fittingbay', x: 0, y: 6, w: 12, h: 6 },
      { i: 'gilliland', x: 8, y: 12, w: 4, h: 6 },
      { i: 'now', x: 0, y: 12, w: 8, h: 4 }, { i: 'ai', x: 8, y: 18, w: 4, h: 9 },
      { i: 'debrief', x: 0, y: 16, w: 4, h: 4 }, { i: 'actions', x: 4, y: 16, w: 4, h: 6 },
      { i: 'pulse', x: 0, y: 20, w: 4, h: 5 }, { i: 'money', x: 4, y: 22, w: 4, h: 2 },
      { i: 'clients', x: 0, y: 25, w: 8, h: 4 }, { i: 'wins', x: 0, y: 29, w: 8, h: 3 },
      { i: 'parking', x: 0, y: 32, w: 8, h: 2 },
    ],
    md: [{ i: 'quotepipeline', x: 0, y: 0, w: 8, h: 6 }, { i: 'fittingbay', x: 0, y: 6, w: 8, h: 6 }],
    sm: [{ i: 'quotepipeline', x: 0, y: 0, w: 2, h: 6 }, { i: 'fittingbay', x: 0, y: 6, w: 2, h: 6 }],
  },
  removed: [], notes: '',
};

// The 5pm debrief — the real backend shape (matches GET /api/admin/desk/debrief).
const _today = () => new Date().toISOString().slice(0, 10);
const DEBRIEF = {
  connected: true, after5: true, today: _today(), date: _today(),
  yesterday_headline: 'Quiet arvo — one quote out, parts chased.',
  digest: {
    conv_count: 3, quiet: false,
    narrative_summary:
      'You spent today across the workshop and the phones — fitted the 79 Series bullbar, chased Patrol suspension parts, and had a walk-in about a full canopy build. Three worth capturing:',
    promises: [
      { promise_id: 'p0', what: 'Book Coastal fleet fit + coordinate loan vehicle', to_whom: 'Coastal Diesel', deadline_heard: 'this week', deadline_date: null, actioned: false, source_quote: "I'll sort the loan car and get you booked in", confidence: 0.85 },
    ],
    key_moments: ['79 Series bullbar fitted', 'Patrol parts ETA slipped to Tuesday', 'Walk-in re full canopy build'],
    people: [{ name: 'Jarrah W.', context: 'walk-in — full canopy build enquiry' }],
    actions: [{ what: 'Order diff breathers for the Patrol build', to_whom: null, deadline_date: null }],
  },
  owed: { glow: false, total: 1, top: [
    { promise_id: 'p0', what: 'Book Coastal fleet fit', person_name: 'Coastal Diesel', person_id: null, date: _today(), age_days: 0, overdue: false, deadline_date: null, actioned: false },
  ] },
};
const ACTIONS_TODAY = { actions: [
  { date: _today(), row_id: 'a0', kind: 'action', what: 'Order diff breathers for the Patrol build', route_hint: 'material', confidence: 0.9, to_whom: null, person_match: null, deadline_date: null, deadline_heard: null, age_days: 0, source_quote: 'grab those diff breathers' },
  { date: _today(), row_id: 'a1', kind: 'action', what: 'Call Coastal Diesel re: staged billing', route_hint: 'callback', confidence: 0.85, to_whom: 'Coastal Diesel', person_match: null, deadline_date: null, deadline_heard: null, age_days: 0, source_quote: null },
  { date: _today(), row_id: 'a2', kind: 'action', what: 'Send canopy quote to Jarrah (walk-in)', route_hint: 'quote', confidence: 0.8, to_whom: 'Jarrah', person_match: null, deadline_date: null, deadline_heard: 'tomorrow', age_days: 0, source_quote: null },
] };

// Connector statuses for the Admin → Connectors page (preview only).
const CONNECTORS_STATUS = {
  connectors: [
    { id: 'xero', status: 'connected', connected: true, account_label: 'TravelTrucks Pty Ltd', last_synced_at: '9m ago' },
    { id: 'stripe', status: 'disconnected', connected: false },
    { id: 'workflowmax', status: 'disconnected', connected: false },
    { id: 'ghl', status: 'attention', connected: false, account_label: 'Key expired — reconnect' },
    { id: 'microsoft', status: 'connected', connected: true, account_label: 'col@traveltrucks.com.au', last_synced_at: '2m ago' },
    { id: 'fieldy', status: 'connected', connected: true, account_label: 'Webhook active', last_synced_at: 'today' },
  ],
};

// Quote Pipeline board (preview only) — the five TravelTrucks stages.
const _qp = (quote_id, title, customer, total_inc, age_days) => ({ quote_id, title, customer, total_inc, age_days });
const QUOTE_PIPELINE = {
  stages: [
    { stage: 'proposal_done', label: 'Proposal done', weight: 0.20, count: 2, cards: [
      _qp('q1', 'Hilux N80 canopy fit-out', 'J. Blake', 8450, 2),
      _qp('q2', 'Ranger dual-battery + lithium', 'Coastal Diesel', 2180, 3) ] },
    { stage: 'col_reviewed', label: 'Col reviewed', weight: 0.35, count: 1, cards: [
      _qp('q3', '79 Series bullbar + winch', 'M. Reid', 6900, 1) ] },
    { stage: 'quote_sent', label: 'Quote sent', weight: 0.55, count: 2, cards: [
      _qp('q4', 'LC200 suspension lift 2"', 'Base T.', 5311, 0),
      _qp('q5', 'Patrol Y62 snorkel pack', 'GT Refrigeration', 1240, 1) ] },
    { stage: 'revision_required', label: 'Revision required', weight: 0.45, count: 1, cards: [
      _qp('q6', 'Coastal Diesel — fleet bars ×4', 'Coastal Diesel', 19800, 4) ] },
    { stage: 'quote_accepted', label: 'Quote accepted', weight: 1.0, count: 1, cards: [
      _qp('q7', 'Bayside 4x4 recovery kit', 'Bayside 4x4', 3420, 1) ] },
  ],
};

// Fitting-bay board (preview only) — the five build stages.
const _bay = (job_id, vehicle, customer, bay, parts) => ({ job_id, vehicle, customer, bay, parts_outstanding: parts });
const FITTING_BAY = {
  stages: [
    { stage: 'parts_ordered', label: 'Parts ordered', count: 2, cards: [
      _bay('j1', 'Patrol Y62 snorkel + diff breathers', 'GT Refrigeration', null, true),
      _bay('j2', 'Ranger dual-battery + lithium', 'Coastal Diesel', null, true) ] },
    { stage: 'in_bay', label: 'In bay', count: 1, cards: [
      _bay('j3', '79 Series bullbar + winch', 'M. Reid', 2, false) ] },
    { stage: 'fitting', label: 'Fitting', count: 1, cards: [
      _bay('j4', 'LC200 suspension lift 2"', 'Base T.', 1, false) ] },
    { stage: 'qa', label: 'QA', count: 1, cards: [
      _bay('j5', 'Hilux N80 canopy + drawers', 'J. Blake', 3, false) ] },
    { stage: 'ready_for_pickup', label: 'Ready for pickup', count: 1, cards: [
      _bay('j6', 'Bayside 4x4 recovery kit', 'Bayside 4x4', null, false) ] },
  ],
};

// Gilliland World (preview only) — Col's private trips / plans / renos.
const _plus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const GILLILAND = {
  tabs: {
    trips: [
      { item_id: 'pi1', title: 'Cape York — dry season', sub: 'Aug · convoy of 3', date: _plus(24), done: false },
      { item_id: 'pi2', title: 'Fraser weekender', sub: 'Ferry booked · tide check', date: _plus(6), done: false },
    ],
    plans: [
      { item_id: 'pi3', title: 'Re-gas the 79 fridge', done: false },
      { item_id: 'pi4', title: 'Sort long-range tank quote', done: true },
    ],
    renos: [
      { item_id: 'pi5', title: 'Deck — replace two boards', done: false },
      { item_id: 'pi6', title: 'Shed shelving', done: false },
    ],
  },
};

export function installPreviewMock() {
  if (typeof window === 'undefined' || window.__ttMockInstalled) return;
  window.__ttMockInstalled = true;
  const origFetch = window.fetch.bind(window);
  const J = (obj, status = 200) =>
    Promise.resolve(new Response(JSON.stringify(obj), { status, headers: { 'Content-Type': 'application/json' } }));

  window.fetch = (url, opt) => {
    const u = String(url);
    if (!/\/api\/admin\//.test(u)) return origFetch(url, opt); // only intercept the admin API
    if (u.includes('/api/admin/desk/board')) return J(BOARD);
    if (u.includes('/api/admin/desk/lists')) return J(LISTS);
    if (u.includes('/api/admin/desk/profiles')) return J(PROFILES);
    if (u.includes('/api/admin/desk/layout')) return J(LAYOUT);
    if (u.includes('/api/admin/desk/debrief/dates')) return J({ dates: [_today()] });
    if (u.includes('/api/admin/desk/debrief')) return (!opt || (opt.method || 'GET') === 'GET') ? J(DEBRIEF) : J({ ok: true });
    if (u.includes('/api/admin/desk/actions/today')) return J(ACTIONS_TODAY);
    if (u.includes('/api/admin/desk/actions/')) return J({ ok: true, count: 1, created: [{ route: 'task', person_match: null, project_name: null }] });
    if (u.includes('/api/admin/connectors') && (!opt || (opt.method || 'GET') === 'GET')) return J(CONNECTORS_STATUS);
    if (u.includes('/api/admin/desk/widgets/quote_pipeline/move')) return J({ ok: true });
    if (u.includes('/api/admin/desk/widgets/quote_pipeline')) return J(QUOTE_PIPELINE);
    if (u.includes('/api/admin/desk/widgets/bays/move')) return J({ ok: true });
    if (u.includes('/api/admin/desk/widgets/bays')) return J(FITTING_BAY);
    if (u.includes('/api/admin/desk/widgets/gilliland')) {
      return (!opt || (opt.method || 'GET') === 'GET') ? J(GILLILAND) : J({ ok: true });
    }
    // Widget tiles + everything else: honest empty (tiles show "not connected").
    return J({});
  };

  // eslint-disable-next-line no-console
  console.log('%cTravelTrucks preview: demo data active (no backend)', 'color:#EC7C24;font-weight:700');
}
